import {
  CommonEventNames,
  CustomEventNames,
  FacebookPixelEventNames,
  GiosgEventNames,
  GoogleAnalytics4EventNames,
  KCOEventNames,
} from './AnalyticsEventNames';

/**
 * @namespace GoogleAnalytics4Events
 */

/**
 * @typedef AnalyticsItem
 * @type {Object}
 * @property {Product} product
 * @property {number} quantity
 * @property {number} position
 * @property {number|string} price
 * @property {number} newQuantity
 * @property {string} activeProductId
 * @property {string} itemListId
 * @property {string} itemListName
 * @property {string} affiliation
 * @property {string} creativeName
 * @property {string} creativeSlot
 */

export default class AnalyticsEvents {
  constructor(currencyStore, uiStore) {
    this.currencyStore = currencyStore;
    this.uiStore = uiStore;
  }

  /**
   * Dispatch analytics events for integrations to use.
   *
   * @param {string} name
   * @param {Object} payload
   */
  dispatchEvent(name, payload, eventData) {
    const detail = {
      detail: {
        name,
        payload: {
          currencyCode: this.currencyStore.currencyCode,
          eventData,
          ...payload,
        },
      },
    };

    // Dispatch a specific event for integrations that only need specific events.
    this.dispatchSpecificEvent(name, detail);
    // Dispatch a catch-all event for integrations that use all events.
    this.dispatchAnalyticsEvent(detail);
  }

  /**
   *
   * @param {string} name
   * @param {Object} detail
   */
  dispatchSpecificEvent(name, detail) {
    const event = new CustomEvent(`analytics:${name}`, detail);
    document.dispatchEvent(event);
  }

  /**
   *
   * @param {Object} detail
   */
  dispatchAnalyticsEvent(detail) {
    const event = new CustomEvent('analytics', detail);
    document.dispatchEvent(event);
  }

  /**
   * @param {Object} data
   * @param {Array.<AnalyticsItem>} data.productList
   * @param {number} data.value
   */
  addPaymentInfo({ productList, value }) {
    this.dispatchEvent(GoogleAnalytics4EventNames.addPaymentInfo, {
      productList,
      value,
    });
  }

  /**
   * @param {Object} data
   * @param {Array.<AnalyticsItem>} data.productList
   * @param {number} data.value
   */
  addShippingInfo({ productList, value }) {
    this.dispatchEvent(GoogleAnalytics4EventNames.addShippingInfo, {
      productList,
      value,
    });
  }

  /**
   * @param {Array.<AnalyticsItem>} productList
   * @param {number} value
   */
  addToCart(productList, value) {
    this.dispatchEvent(GoogleAnalytics4EventNames.addToCart, {
      productList,
      value,
    });
  }

  /**
   * @param {Array.<AnalyticsItem>} productList
   * @param {number} value
   */
  addToProposal(productList, value) {
    this.dispatchEvent(GoogleAnalytics4EventNames.addToProposal, {
      productList,
      value,
    });
  }

  /**
   * @param {Array.<AnalyticsItem>} productList
   * @param {number} value
   */
  addToWishlist(productList, value) {
    this.dispatchEvent(GoogleAnalytics4EventNames.addToWishlist, {
      productList,
      value,
    });
  }

  /**
   * @param {Array.<AnalyticsItem>} productList
   * @param {Object} actionField
   * @param {number} actionField.step
   * @param {string} actionField.option
   * @param {number} value
   */
  beginProposal(productList, actionField, value) {
    this.dispatchEvent(GoogleAnalytics4EventNames.beginProposal, {
      productList,
      actionField,
      value,
    });
  }

  /**
   * Send all products from the cart.
   */
  cartStatus(cart) {
    this.dispatchEvent(GiosgEventNames.cartStatus, {
      cart,
    });
  }

  /**
   * Clear all products from the cart.
   */
  clearCart() {
    this.dispatchEvent(GiosgEventNames.clearCart, {});
  }

  /**
   * @param {Array.<AnalyticsItem>} productList
   * @param {Object} actionField
   * @param {number} actionField.step
   * @param {string} actionField.option
   * @param {number} value
   * @param {string} coupon
   */
  beginCheckout(productList, actionField, value, coupon) {
    this.dispatchEvent(GoogleAnalytics4EventNames.beginCheckout, {
      productList,
      actionField,
      value,
      coupon,
    });
  }

  checkoutOption() {
    throw notImplementedError;
  }

  /**
   * @param {Array.<AnalyticsItem>} productList
   * @param {number} value monetary value of the event
   */
  customizeProduct(productList, value) {
    this.dispatchEvent(FacebookPixelEventNames.customizeProduct, {
      productList,
      value,
    });
  }

  /**
   * @param {number} value monetary value of the event
   * @param {string} lead_source origin of the lead
   */
  generateLead(value, lead_source) {
    this.dispatchEvent(GoogleAnalytics4EventNames.generateLead, {
      value,
      lead_source,
    });
  }

  /**
   * @param {string} groupId
   */
  joinGroup(groupId) {
    this.dispatchEvent(GoogleAnalytics4EventNames.joinGroup, {
      groupId,
    });
  }

  // KCO (Kustom Checkout) analytics methods
  /**
   * @param {Object} eventData - KCO load event data
   */
  kcoLoad(eventData) {
    this.dispatchEvent(KCOEventNames.load, eventData);
  }

  /**
   * @param {Object} eventData - KCO customer event data
   */
  kcoCustomer(eventData) {
    this.dispatchEvent(KCOEventNames.customer, eventData);
  }

  /**
   * @param {Object} eventData - KCO change event data
   */
  kcoChange(eventData) {
    this.dispatchEvent(KCOEventNames.change, eventData);
  }

  /**
   * @param {Object} eventData - KCO billing address change event data
   */
  kcoBillingAddressChange(eventData) {
    this.dispatchEvent(KCOEventNames.billing_address_change, eventData);
  }

  /**
   * @param {Object} eventData - KCO shipping address change event data
   */
  kcoShippingAddressChange(eventData) {
    this.dispatchEvent(KCOEventNames.shipping_address_change, eventData);
  }

  /**
   * @param {Object} eventData - KCO shipping option change event data
   */
  kcoShippingOptionChange(eventData) {
    this.dispatchEvent(KCOEventNames.shipping_option_change, eventData);
  }

  /**
   * @param {Object} eventData - KCO order total change event data
   */
  kcoOrderTotalChange(eventData) {
    this.dispatchEvent(KCOEventNames.order_total_change, eventData);
  }

  /**
   * @param {Object} eventData - KCO checkbox change event data
   */
  kcoCheckboxChange(eventData) {
    this.dispatchEvent(KCOEventNames.checkbox_change, eventData);
  }

  /**
   * @param {Object} eventData - KCO network error event data
   */
  kcoNetworkError(eventData) {
    this.dispatchEvent(KCOEventNames.network_error, eventData);
  }

  /**
   * @param {boolean} eventData - KCO redirect initiated event data
   */
  kcoRedirectInitiated(eventData) {
    this.dispatchEvent(KCOEventNames.redirect_initiated, eventData);
  }

  /**
   * @param {Object} eventData - KCO load confirmation event data
   */
  kcoLoadConfirmation(eventData) {
    this.dispatchEvent(KCOEventNames.load_confirmation, eventData);
  }

  /**
   * @param {string} method
   */
  login(method) {
    this.dispatchEvent(GoogleAnalytics4EventNames.login, {
      method,
    });
  }

  paymentStart() {
    this.dispatchEvent(CustomEventNames.payment_start);
  }

  /**
   * @param {Order} currentOrder Current order model.
   */
  proposalSent(currentOrder) {
    this.dispatchEvent(GoogleAnalytics4EventNames.proposalSent, {
      currentOrder,
    });
  }

  /**
   * @param {string} listName
   * @param {Array.<AnalyticsItem>} productList
   * @param {string} listId
   */
  selectItem(listName, productList, listId) {
    this.dispatchEvent(GoogleAnalytics4EventNames.selectItem, {
      listName,
      productList,
      listId,
    });
  }

  /**
   * @param {Array.<AnalyticsItem>} bannerList
   */
  selectPromotion(bannerList) {
    this.dispatchEvent(GoogleAnalytics4EventNames.selectPromotion, {
      bannerList,
    });
  }

  /**
   * @param {Order} currentOrder Current order model.
   */
  purchase(currentOrder) {
    this.dispatchEvent(GoogleAnalytics4EventNames.purchase, {
      currentOrder,
    });
  }

  /**
   * @param {Object} gdprInfo
   * @param {string} gdprInfo.first_name
   * @param {string} gdprInfo.last_name
   * @param {string} gdprInfo.email
   * @param {string} gdprInfo.street_address
   * @param {string} gdprInfo.international_number
   * @param {boolean} gdprInfo.marketing_ban
   */
  sendGdprData(gdprInfo) {
    this.dispatchEvent(CustomEventNames.sendGdprData, gdprInfo);
  }

  /**
   * @param {Object} data
   * @param {Array<CartProduct>} data.productList List of removed products
   * @param {number} data.value a Monetary value of the event
   */
  removeFromCart({ productList, value }) {
    this.dispatchEvent(GoogleAnalytics4EventNames.removeFromCart, {
      productList,
      value,
    });
  }

  /**
   * @param {number} value
   * @param {Array.<Product>} productList
   */
  removeFromProposal({ value, productList }) {
    this.dispatchEvent(GoogleAnalytics4EventNames.removeFromProposal, {
      value,
      productList,
    });
  }

  /**
   * @param {string} searchTerm
   */
  search(searchTerm) {
    this.dispatchEvent(GoogleAnalytics4EventNames.search, {
      searchTerm,
    });
  }

  /**
   * @param {Object} content
   * @param {string} content.method
   * @param {string} content.content_type
   * @param {string} content.item_id
   */
  share(content) {
    this.dispatchEvent(GoogleAnalytics4EventNames.share, {
      content,
    });
  }

  /**
   * @param {string} method
   */
  signup(method) {
    this.dispatchEvent(GoogleAnalytics4EventNames.signup, {
      method,
    });
  }

  /**
   * @param {Object} data
   * @param {number} data.value
   * @param {Array.<AnalyticsItem>} data.productList
   */
  viewItem({ value, productList }) {
    this.dispatchEvent(GoogleAnalytics4EventNames.viewItem, {
      value,
      productList,
    });
  }

  /**
   * @param {string} listName
   * @param {Array.<AnalyticsItem} productList
   * @param {string} listId
   */
  viewItemList(listName, productList, listId) {
    this.dispatchEvent(GoogleAnalytics4EventNames.viewItemList, {
      listId,
      listName,
      productList,
      pageName: this.uiStore.currentPage,
    });
  }

  /**
   * @param {Array.<AnalyticsItem>} bannerList An array with objects in the format { bannerZone, banner }.
   */
  viewPromotion(bannerList) {
    this.dispatchEvent(GoogleAnalytics4EventNames.viewPromotion, {
      bannerList,
    });
  }

  refund() {
    throw notImplementedError;
  }

  pageNotFound(payload) {
    this.dispatchEvent(CustomEventNames.pageNotFound, payload);
  }

  /**
   * @param {string} pageName
   */
  setPage(pageName) {
    this.dispatchEvent(CommonEventNames.setPage, {
      pageName,
    });
  }

  /**
   * When quick search suggestions are selected, the information is sent through this function.
   * Can be used to train suggestions.
   *
   * @param searchText
   * @param suggestionType
   * @param suggestionValue
   */
  suggestionSelection(searchText, suggestionType, suggestionValue) {
    this.dispatchEvent(CustomEventNames.suggestionSelection, {
      searchText,
      suggestionType,
      suggestionValue,
    });
  }

  /**
   * @param {Object} data
   * @param {Array.<AnalyticsItem>} data.productList
   * @param {number} data.value
   */
  viewCart({ productList, value, eventData = null }) {
    this.dispatchEvent(
      GoogleAnalytics4EventNames.viewCart,
      {
        productList,
        value,
      },
      eventData
    );
  }

  /**
   * @param {Object} data
   * @param {Array.<AnalyticsItem>} data.productList
   * @param {number} data.value
   */
  viewItem({ productList, value }) {
    this.dispatchEvent(GoogleAnalytics4EventNames.viewItem, {
      productList,
      value,
    });
  }

  /**
   * @param {number} value
   * @param {Array.<AnalyticsItem>} productList
   */
  viewProposal(value, productList) {
    this.dispatchEvent(GoogleAnalytics4EventNames.viewProposal, {
      value,
      productList,
    });
  }
}
